import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { getEdgeFunctionStatus, invokeEdgeFunction, isEdgeFunctionAuthError } from "@/lib/edge-functions";
import {
  isSupabasePermissionError,
  isSupabaseSchemaError,
  isSupabaseUnavailableError,
} from "@/lib/supabase-compat";

export interface CurrentPerson {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  organization_id: string;
  household_id: string | null;
  relationship: string | null;
  status?: string | null;
  photo_url?: string | null;
  communication_pref?: string | null;
}

const bootstrappedOwnerAccounts = new Set<string>();
const permanentlyUnavailableBootstrapAccounts = new Set<string>();

type UnknownEdgeFunctionError = {
  status?: number;
  context?: {
    status?: number;
  };
};

const isPermanentBootstrapFailure = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  if (isEdgeFunctionAuthError(error)) return true;
  const status = getEdgeFunctionStatus(error as UnknownEdgeFunctionError);
  if (status) {
    return status >= 400 && status < 500;
  }

  const message = String((error as { message?: unknown }).message ?? "").toLowerCase();
  const statusFromMessage = message.match(/status\s*code\s*[:=]\s*(\d{3})/)?.[1];
  if (!statusFromMessage) return false;
  const parsed = Number.parseInt(statusFromMessage, 10);
  return Number.isNaN(parsed) ? false : parsed >= 400 && parsed < 500;
};

const isExpectedBootstrapAccessDenied = (error: unknown) => {
  if (!error || typeof error !== "object") return false;
  const message = String((error as { message?: unknown }).message ?? "").toLowerCase();
  if (message.includes("not authorized to bootstrap org owner")) {
    return true;
  }
  const status = getEdgeFunctionStatus(error as UnknownEdgeFunctionError);
  return status === 403;
};

export function useCurrentPerson(options?: { cacheScope?: string }) {
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  const queryResult = useQuery({
    queryKey: ["current-person", organizationId, user?.id, ...(options?.cacheScope === undefined ? [] : [{ cacheScope: options.cacheScope }])],
    queryFn: async () => {
      if (!organizationId || !user?.id) return { person: null, wasUnavailable: false };

      const { data, error } = await supabase
        .from("people")
        .select("id, first_name, last_name, email, phone, organization_id, household_id, relationship, status, photo_url, communication_pref")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          // Swallowed to null rather than thrown so a transient schema/
          // permission/5xx blip doesn't error every screen that depends on
          // "who am I" — but that makes it indistinguishable from a
          // genuine "no person record" null to a consumer reading `data`
          // alone. wasUnavailable (below) is the signal for a consumer
          // that needs to tell the two apart (e.g. don't silently treat an
          // outage as "you have nothing to do").
          return { person: null, wasUnavailable: true };
        }
        throw error;
      }
      return { person: data as CurrentPerson | null, wasUnavailable: false };
    },
    enabled: !!organizationId && !!user?.id,
  });

  useEffect(() => {
    if (!organizationId || !user?.id) return;
    if (queryResult.isLoading || queryResult.isFetching) return;
    const bootstrapKey = `${organizationId}:${user.id}`;
    if (bootstrappedOwnerAccounts.has(bootstrapKey) || permanentlyUnavailableBootstrapAccounts.has(bootstrapKey)) return;

    const shouldBootstrap =
      queryResult.data?.person === null ||
      !queryResult.data?.person?.status ||
      queryResult.data?.person?.status === "visitor";

    if (!shouldBootstrap) return;

    bootstrappedOwnerAccounts.add(bootstrapKey);
    const bootstrapOwner = async () => {
      const { data: membership, error: membershipError } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (membershipError) {
        if (!isSupabaseSchemaError(membershipError) && !isSupabasePermissionError(membershipError)) {
          console.warn("Failed to verify org role for bootstrap:", membershipError);
        }
        return;
      }

      if (!membership || !["owner", "admin"].includes(membership.role)) {
        return;
      }

      const { error: bootstrapError } = await invokeEdgeFunction("bootstrap-org-owner", {
        body: { organizationId },
      });

      if (bootstrapError) {
        if (isExpectedBootstrapAccessDenied(bootstrapError)) {
          permanentlyUnavailableBootstrapAccounts.add(bootstrapKey);
          console.debug("Bootstrap owner not permitted for current membership:", bootstrapError);
          return;
        }
        if (isPermanentBootstrapFailure(bootstrapError)) {
          permanentlyUnavailableBootstrapAccounts.add(bootstrapKey);
          console.warn("Session expired while bootstrapping owner:", bootstrapError);
          return;
        }
        console.warn("Failed to bootstrap owner person record:", bootstrapError);
        return;
      }

      queryResult.refetch();
    };

    bootstrapOwner().catch((error) => {
      console.error("Unexpected error bootstrapping owner:", error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend on specific queryResult properties
  }, [
    organizationId,
    queryResult.data,
    queryResult.isFetching,
    queryResult.isLoading,
    queryResult.refetch,
    user?.id,
  ]);

  // Every existing consumer destructures `data`/`isLoading`/etc. off this
  // return, so `data` keeps resolving to CurrentPerson | null | undefined
  // exactly as before — only wasUnavailable is new, additive, and safe for
  // an existing caller to ignore.
  return {
    ...queryResult,
    data: queryResult.data === undefined ? undefined : queryResult.data.person,
    wasUnavailable: queryResult.data?.wasUnavailable ?? false,
  };
}
